import * as z from "zod";

export const testExtensionArtifactErrorStage = z.enum([
  "typeCheck",
  "inputValidation",
  "execution",
  "outputValidation",
]);
export type TestExtensionArtifactErrorStage = z.infer<
  typeof testExtensionArtifactErrorStage
>;

/**
 * Run candidate artifact code once against a caller-supplied input, without
 * storing anything. The editor's "Test" tab uses this so authors can iterate
 * on code against editable sample data before saving.
 *
 * The run mirrors the production pipeline (verify → type-check → execute in
 * the RedoJS sandbox → validate output), so a passing test run is the same
 * code path a saved artifact takes at invoke time. Expected failures (type
 * errors, invalid input, runtime throws, bad output shape) come back as
 * `ok: false` with the failing stage so the editor can render them inline
 * rather than as an RPC error.
 */
export const testExtensionArtifact = {
  name: "testExtensionArtifact" as const,
  input: z.object({
    contractId: z.string(),
    code: z.string(),
    inputSelector: z.array(z.string()),
    // Optional because JSON serialization drops undefined-valued keys; the
    // contract's input schema is what actually constrains the value.
    input: z.unknown().optional(),
    /** What the host knows about this artifact's Input and Output (see Contract.specialize). */
    typeShapes: z
      .object({ input: z.unknown().optional(), output: z.unknown().optional() })
      .optional(),
  }),
  output: z.object({
    ok: z.boolean(),
    /** Console output captured during the run ("" if none or never ran). */
    logs: z.string(),
    /** The function's validated return value; absent on failure. */
    output: z.unknown().optional(),
    error: z
      .object({ stage: testExtensionArtifactErrorStage, message: z.string() })
      .optional(),
  }),
};
